// Single source of truth for the /tools/real-affordability guide:
// the gap between what a lender approves and what a life actually supports.

export const PAGE_PATH = "/tools/real-affordability";
export const PAGE_URL = "https://www.homeapproach.com/tools/real-affordability";
export const PAGE_TITLE = "What Home Payment Can I Actually Afford?";
export const PAGE_DESCRIPTION =
  "Lenders qualify you on gross income and a debt ratio. A livable budget starts with net income. See the 25% comfort target, the 30% ceiling, the ghost expenses DTI ignores, and a calculator that shows your real monthly housing number.";

/** Budgeting benchmarks used by both the copy and the calculator. */
export const COMFORT_RATE = 0.25;
export const CEILING_RATE = 0.30;
export const LENDER_RATE = 0.36; // typical back-end DTI applied to gross
export const MAINTENANCE_RATE = 0.01; // 1% of home value per year

export type GhostExpense = {
  id: string;
  label: string;
  detail: string;
  rule: string;
};

export const GHOST_EXPENSES: GhostExpense[] = [
  {
    id: "maintenance",
    label: "Maintenance and repairs",
    detail:
      "Nothing in a house is somebody else's problem anymore. Water heaters, roofs, HVAC, appliances, and the small things that add up between the big things. Some years you spend nothing and some years you spend eleven thousand dollars at once, so it has to be a monthly line either way.",
    rule: "Budget roughly 1% of the home's value per year — about $333/month on a $400,000 home.",
  },
  {
    id: "utilities",
    label: "The utility jump",
    detail:
      "A house is bigger than the apartment, it has more exterior wall, and the bills that used to be bundled into rent are now separate: water, sewer, trash, sometimes gas. Renters routinely underestimate this by half.",
    rule: "Expect $100 to $300 more per month than you pay renting, more in an older or larger home.",
  },
  {
    id: "hoa",
    label: "HOA and community fees",
    detail:
      "If the listing has an HOA, that payment never goes away and it does not stay flat. Dues rise, and special assessments arrive with no warning when the community needs a new roof or a repaved lot.",
    rule: "Include the full current dues, and assume increases over the years you'll own.",
  },
  {
    id: "insurance-taxes",
    label: "Taxes and insurance creep",
    detail:
      "PITI is principal, interest, taxes, and insurance. The P and I are fixed on a fixed-rate loan. The T and I are not — reassessments and premium hikes raise your escrow, which raises your payment after closing.",
    rule: "Assume your escrow portion drifts upward every year, even on a fixed-rate mortgage.",
  },
  {
    id: "childcare",
    label: "Childcare and dependents",
    detail:
      "Underwriting does not count daycare, tuition, or supporting a parent as debt, because none of it shows on a credit report. It is often the single largest number in a household's budget after housing.",
    rule: "Enter it as a hard monthly commitment, not a flexible expense.",
  },
  {
    id: "savings",
    label: "Retirement and emergency savings",
    detail:
      "A lender treats the money you save as money available for a mortgage payment. Stopping your contributions to make the payment work is technically qualifying and practically a bad trade.",
    rule: "Protect the contribution first, then see what payment is left over.",
  },
  {
    id: "lifestyle",
    label: "Lifestyle and subscriptions",
    detail:
      "Groceries, gas, travel, streaming, the gym, eating out, gifts. DTI ignores every dollar of it. This is the category that quietly turns an approved payment into a house-poor one.",
    rule: "Total what you actually spend for a month — not what you intend to spend.",
  },
];

export type Section = { id: string; label: string };

export const CONTENTS: Section[] = [
  { id: "net-income", label: "Start with net income, not gross" },
  { id: "calculator", label: "Calculate your real number" },
  { id: "ghost-expenses", label: "Ghost expenses" },
  { id: "unseen-life", label: "The unseen life a lender can't see" },
  { id: "trial-run", label: "The mortgage trial run" },
  { id: "faq", label: "Common questions" },
  { id: "assumptions", label: "Assumptions and disclaimer" },
];

export type TrialStep = { id: string; title: string; detail: string };

export const TRIAL_STEPS: TrialStep[] = [
  {
    id: "pick",
    title: "Pick the number you're testing",
    detail:
      "Use the all-in figure from the calculator — PITI plus maintenance, utilities, and HOA — not the mortgage payment alone. That is the number you'd actually live with.",
  },
  {
    id: "transfer",
    title: "Automate the difference into savings",
    detail:
      "Each month, move the gap between that number and your current rent into a separate savings account on payday. Automatic, not manual, so you can't quietly skip a month.",
  },
  {
    id: "live",
    title: "Live on what's left for 3 to 4 months",
    detail:
      "One month proves nothing. Three or four covers an irregular expense, a car repair, a holiday, and the ordinary drift in spending that makes or breaks the budget.",
  },
  {
    id: "review",
    title: "Judge it honestly, then adjust",
    detail:
      "Did you touch the savings? Carry a credit card balance? Skip a retirement contribution? Any of those is the number telling you it's too high. Lower the target payment and run it again — and either way you finish with a bigger down payment.",
  },
];

export type Faq = { id: string; q: string; a: string };

export const FAQS: Faq[] = [
  {
    id: "costs-banks-ignore",
    q: "What costs do banks ignore when they approve you?",
    a: "A lender's debt-to-income calculation counts your gross income against your PITI and the debts that appear on your credit report — car loans, student loans, credit cards. It ignores taxes withheld from your paycheck, health premiums, retirement contributions, childcare, tuition, groceries, utilities, commuting, home maintenance, and every subscription you pay. That is why an approval amount is a ceiling on the loan, not a recommendation for a budget.",
  },
  {
    id: "net-vs-gross",
    q: "Should I use net or gross income to figure out what I can afford?",
    a: "Net. Gross income is what the lender qualifies you on; net income is the money that actually reaches your account and pays the mortgage. Depending on your tax bracket, benefits, and retirement contributions, net pay is commonly 25% to 35% lower than gross — so a payment that looks like 28% of gross can quietly be 40% of what you actually take home.",
  },
  {
    id: "twenty-five-percent",
    q: "Why 25% of net income instead of the 28% rule?",
    a: "The familiar 28% and 36% rules are measured against gross income, which is the lender's frame. Measured against net pay, a 25% housing target leaves room for the ownership costs underwriting ignores, and 30% is a practical ceiling rather than a goal. Above 30% of net, most households find that maintenance, savings, or both get squeezed first.",
  },
  {
    id: "one-percent-rule",
    q: "What is the 1% maintenance rule?",
    a: "A common planning shortcut: set aside about 1% of the home's value each year for maintenance and repairs — roughly $333 a month on a $400,000 home. It's an average, not a bill. Older homes, larger homes, and homes with deferred maintenance run higher; a newer build under warranty may run lower for the first few years, then catch up.",
  },
  {
    id: "trial-run",
    q: "How do I trial run a mortgage payment before buying?",
    a: "For three to four months, automatically transfer the difference between your current rent and your projected all-in housing cost into a separate savings account on payday, then live on what remains. If you never dip into that account, carry a new credit card balance, or pause retirement contributions, the payment is realistic. If you do, lower the number. Either way you end the test with a larger down payment.",
  },
  {
    id: "piti",
    q: "What does PITI include?",
    a: "Principal, interest, taxes, and insurance — the four parts of a typical escrowed mortgage payment. It does not include HOA dues, mortgage insurance in every quote, utilities, or maintenance, so a PITI quote is the floor of your monthly housing cost rather than the total.",
  },
];

export const ASSUMPTIONS = [
  "The 25% comfort target and 30% ceiling are applied to net (take-home) monthly income, not gross. They are common budgeting benchmarks, not underwriting rules, and no lender uses them to approve a loan.",
  "The 1% maintenance figure is an annual planning average based on home value. Actual repair costs vary widely by age, condition, climate, and luck.",
  "The lender comparison shown in the calculator applies a 36% total debt ratio to estimated gross income for illustration only. Your actual approval depends on the loan program, credit score, down payment, reserves, and the debts on your credit report.",
  "This page is educational budgeting guidance. It is not lending, tax, investment, or legal advice, and it is not a loan offer, pre-qualification, or commitment to lend. Talk to a licensed loan officer and a tax professional about your own situation.",
];

const SITE = "https://www.homeapproach.com";

/** WebPage + BreadcrumbList + FAQPage + HowTo, baked into the server HTML via routeMeta. */
export const REAL_AFFORDABILITY_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "HowTo",
      name: "How to trial run a mortgage payment before you buy",
      description:
        "Test a projected all-in housing payment for three to four months before committing to it.",
      totalTime: "P4M",
      step: TRIAL_STEPS.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.detail,
        url: `${PAGE_URL}#trial-run`,
      })),
    },
  ],
};
